// src/utils/AudioContextManager.ts
var AudioContextManager = class _AudioContextManager {
  /**initiates the AudioContext if it hasn't already been constructed.
   * @return a [bool] indicating true if AudioContext.supported*/
  static initContext() {
    if (this._context != null)
      return true;
    else if (AudioContext) {
      _AudioContextManager._context = new AudioContext();
      return _AudioContextManager._context != null;
    }
    return false;
  }
  static get context() {
    return _AudioContextManager._context;
  }
};

// src/utils/AudioProcessor.ts
var AudioProcessor = class _AudioProcessor {
  static generateReverbImpulse(context, seconds = 3, decay = 2, reverse = false) {
    let rate = context.sampleRate;
    let length = rate * seconds;
    let impulse = context.createBuffer(2, length, rate);
    let impulseL = impulse.getChannelData(0);
    let impulseR = impulse.getChannelData(1);
    let i;
    let n;
    for (i = 0; i < length; i++) {
      n = reverse ? length - i : i;
      impulseL[i] = (Math.random() * 2 - 1) * Math.pow(1 - n / length, decay);
      impulseR[i] = (Math.random() * 2 - 1) * Math.pow(1 - n / length, decay);
    }
    return impulse;
  }
  // see https://github.com/jaz303/audio-buffer-utils/blob/master/index.js
  /** trim a stereo AudioBuffer*/
  static trimBuffer(buffer, seconds, context) {
    let leftChannel = buffer.getChannelData(0);
    let rightChannel = buffer.getChannelData(1);
    let startTrimSample = Math.floor(context.sampleRate * seconds);
    let newLength = buffer.length - startTrimSample;
    let newLeftChannel = new Float32Array(newLength);
    let newRightChannel = new Float32Array(newLength);
    let j = 0;
    for (let i = startTrimSample; i < leftChannel.length; i++) {
      newLeftChannel[j] = leftChannel[i];
      j++;
    }
    j = 0;
    for (let i = startTrimSample; i < rightChannel.length; i++) {
      newRightChannel[j] = rightChannel[i];
      j++;
    }
    let returnBuffer = context.createBuffer(
      2,
      newLength,
      context.sampleRate
    );
    returnBuffer.copyToChannel(newLeftChannel, 0);
    returnBuffer.copyToChannel(newRightChannel, 1);
    return returnBuffer;
  }
  static async normalizeWithBreaks(audioBuffer, maxSamplesBetweenBreaks = 441e3, convertToMono = true, normalizationValue = 0.8, minThreshold = 0.1, fadeOutTime = 0) {
    let fadeSamples = fadeOutTime * audioBuffer.sampleRate;
    if (audioBuffer.numberOfChannels == 1) {
      await _AudioProcessor._normalizeMonoBuffer(
        audioBuffer,
        maxSamplesBetweenBreaks,
        normalizationValue,
        minThreshold,
        fadeSamples
      );
    } else {
      audioBuffer = await _AudioProcessor._normalizeMultiChannelBuffer(
        audioBuffer,
        maxSamplesBetweenBreaks,
        convertToMono,
        normalizationValue,
        minThreshold,
        fadeSamples
      );
    }
    return audioBuffer;
  }
  static async _normalizeMonoBuffer(audioBuffer, maxSamplesBetweenBreaks, normalizationValue, minThreshold, fadeSamples) {
    let samples = audioBuffer.getChannelData(0);
    let peak = await _AudioProcessor._getPeak(
      samples,
      maxSamplesBetweenBreaks
    );
    if (peak < minThreshold) {
      return;
    }
    let factor = normalizationValue / peak;
    await _AudioProcessor._multiplyValues(
      samples,
      factor,
      maxSamplesBetweenBreaks,
      fadeSamples
    );
  }
  static async _normalizeMultiChannelBuffer(audioBuffer, maxSamplesBetweenBreaks, convertToMono, normalizationValue, minThreshold, fadeSamples) {
    let peaks = [];
    let highestPeak = 0;
    let nonZeroSampleSets = [];
    for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
      let samples = audioBuffer.getChannelData(i);
      let peak = await _AudioProcessor._getPeak(
        samples,
        maxSamplesBetweenBreaks
      );
      peaks.push(peak);
      if (peak > 0) {
        nonZeroSampleSets.push(samples);
        if (peak > highestPeak) {
          highestPeak = peak;
        }
      }
    }
    if (highestPeak === 0) {
      if (convertToMono) {
        return AudioContextManager.context.createBuffer(
          1,
          audioBuffer.length,
          audioBuffer.sampleRate
        );
      } else {
        return audioBuffer;
      }
    }
    if (!convertToMono) {
      if (highestPeak < minThreshold) {
        return audioBuffer;
      }
      let factor = normalizationValue / highestPeak;
      for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
        let samples = audioBuffer.getChannelData(i);
        await _AudioProcessor._multiplyValues(
          samples,
          factor,
          maxSamplesBetweenBreaks,
          fadeSamples
        );
      }
      return audioBuffer;
    } else {
      let newBuffer = AudioContextManager.context.createBuffer(
        1,
        audioBuffer.length,
        audioBuffer.sampleRate
      );
      let targetSamples = newBuffer.getChannelData(0);
      let factor = highestPeak >= minThreshold ? normalizationValue / highestPeak / nonZeroSampleSets.length : 1;
      await _AudioProcessor._mergeToMono(
        nonZeroSampleSets,
        targetSamples,
        factor,
        maxSamplesBetweenBreaks,
        fadeSamples
      );
      return newBuffer;
    }
  }
  static async _getPeak(samples, maxSamplesBetweenBreaks) {
    let peak = 0;
    let i = 0;
    let limit = Math.min(i + maxSamplesBetweenBreaks, samples.length);
    let sample;
    while (i < samples.length) {
      for (i; i < limit; i++) {
        sample = samples[i];
        if (sample > peak) {
          peak = sample;
        } else if (sample * -1 > peak) {
          peak = sample * -1;
        }
      }
      await _AudioProcessor._waitForNextFrame();
      limit = Math.min(i + maxSamplesBetweenBreaks, samples.length);
    }
    return peak;
  }
  static async _multiplyValues(samples, factor, maxSamplesBetweenBreaks, fadeSamples) {
    let i = 0;
    let numSamples = samples.length;
    let limit = Math.min(i + maxSamplesBetweenBreaks, numSamples);
    while (i < numSamples) {
      for (i; i < limit; i++) {
        if (numSamples - i < fadeSamples) {
          samples[i] *= factor * ((numSamples - i) / fadeSamples);
        } else {
          samples[i] *= factor;
        }
      }
      await _AudioProcessor._waitForNextFrame();
      limit = Math.min(i + maxSamplesBetweenBreaks, samples.length);
    }
  }
  static async _mergeToMono(sampleSets, targetSet, factor, maxSamplesBetweenBreaks, fadeSamples) {
    maxSamplesBetweenBreaks = Math.floor(maxSamplesBetweenBreaks / sampleSets.length);
    let i = 0;
    let numSamples = targetSet.length;
    let limit = Math.min(i + maxSamplesBetweenBreaks, numSamples);
    while (i < numSamples) {
      for (i; i < limit; i++) {
        for (let j = 0; j < sampleSets.length; j++) {
          targetSet[i] += sampleSets[j][i];
        }
        if (numSamples - i < fadeSamples) {
          targetSet[i] *= factor * ((numSamples - i) / fadeSamples);
        } else {
          targetSet[i] *= factor;
        }
      }
      await _AudioProcessor._waitForNextFrame();
      limit = Math.min(i + maxSamplesBetweenBreaks, targetSet.length);
    }
  }
  static _waitForNextFrame() {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve();
      }, 0);
    });
  }
};

// src/utils/MultiSoundLoader.ts
import { createEmitter } from "ts_utils";
var MultiSoundLoader = class {
  constructor(context) {
    this._loadCount = 0;
    this._loadProgressEmitter = createEmitter();
    this._onBufferLoaded = (e) => {
      var request = e.target;
      this._loadProgressEmitter.emit(`${request.responseURL} downloaded... extracting...`);
      this._context.decodeAudioData(request.response).then((buffer) => {
        this._filesMap.set(request, buffer);
        this._loadCount++;
        if (this._loadCount === this._filesMap.size) {
          this._loadProgressEmitter.emit("audio ready");
          this._loadCompleter(Array.from(this._filesMap.values()));
          this._cleanup();
        }
      }).catch((err) => {
        console.log(err);
      });
    };
    this._onBufferLoadError = (e) => {
      this._cleanup();
      this._loadCanceler("Problem downloading one or more files: " + e.toString());
    };
    this._context = context;
    if (this._context == null)
      throw "AudioContext not set";
  }
  get loadProgress() {
    return this._loadProgressEmitter.event;
  }
  load(urlList) {
    this._filesMap = /* @__PURE__ */ new Map();
    this._loadProgressEmitter.emit("downloading audio...");
    return new Promise((resolve, reject) => {
      this._loadCompleter = resolve;
      this._loadCanceler = reject;
      for (let i = 0; i < urlList.length; i++) {
        var request = this._loadBuffer(urlList[i]);
        this._filesMap.set(request, null);
      }
    });
  }
  _loadBuffer(url) {
    let request = new XMLHttpRequest();
    request.responseType = "arraybuffer";
    request.onload = this._onBufferLoaded;
    request.onerror = this._onBufferLoadError;
    request.open("GET", url);
    request.send();
    return request;
  }
  _cleanup() {
    for (let request of this._filesMap.keys()) {
      request.onload = null;
      request.onerror = null;
    }
    this._filesMap = null;
  }
};

// src/utils/phaze/PhazeManager.ts
var PhazeManager = class {
  constructor() {
  }
  async renderAudio(workletUrl, audioBuffer, pitch = 1) {
    try {
      let oac = new OfflineAudioContext({
        length: audioBuffer.length,
        sampleRate: audioBuffer.sampleRate,
        numberOfChannels: audioBuffer.numberOfChannels
      });
      await oac.audioWorklet.addModule(workletUrl);
      let bsn = oac.createBufferSource();
      bsn.buffer = audioBuffer;
      let phazeNode = this.createAudioNode(oac);
      let pitchParam = phazeNode.parameters.get("pitchFactor");
      pitchParam.value = pitch;
      bsn.connect(phazeNode);
      phazeNode.connect(oac.destination);
      bsn.start(0);
      let rendered = await oac.startRendering();
      for (let i = 0; i < rendered.numberOfChannels; i++) {
        let ch = rendered.getChannelData(i);
        for (let j = 0; j < ch.length; j++) {
          if (isNaN(ch[j])) {
            ch[j] = 0;
          }
        }
      }
      return rendered;
    } catch (err) {
      console.log(err);
      return null;
    }
  }
  createAudioNode(context) {
    let node = new AudioWorkletNode(context, "phase-vocoder-processor");
    return node;
  }
};

// src/utils/signalsmith_stretch/SignalSmithManager.ts
var _queue = [];
var _isProcessing = false;
var _SSMJob = class {
};
var _SignalSmithManager = class _SignalSmithManager {
  /**set the SignalSmithManager.iframeUrl first to use iframe for memory
   * management */
  static renderAudio(workletUrl, audioBuffer, semitones = 0, rate = 1) {
    let job = new _SSMJob();
    job.workletUrl = workletUrl;
    job.sourceBuffer = audioBuffer;
    job.semitones = semitones;
    job.rate = rate;
    let promise = new Promise((resolve, reject) => {
      job.resolve = resolve;
      job.reject = reject;
    });
    _queue.push(job);
    if (_SignalSmithManager.iframeUrl != null) {
      _SignalSmithManager._processWithIframe();
    } else {
      _SignalSmithManager._process();
    }
    return promise;
  }
  static _processWithIframe() {
    if (_queue.length < 1) {
      return;
    }
    let job = _queue.shift();
    _isProcessing = true;
    let iframe = document.createElement("iframe");
    iframe.onload = async (e) => {
      iframe.onload = null;
      await iframe.contentWindow.processJob(job);
    };
    iframe.src = _SignalSmithManager.iframeUrl;
    iframe.style.display = "none";
    job.iframeFinishedCallback = () => {
      iframe.remove();
      iframe.src = "";
    };
    document.body.append(iframe);
  }
  static async _process() {
    if (_isProcessing || _queue.length < 1) {
      return;
    }
    let job = _queue.shift();
    _isProcessing = true;
    try {
      let sampleRate = job.sourceBuffer.sampleRate;
      job.numLatencySamples = Math.ceil(sampleRate * 0.12);
      job.adjustedSourceBuffer = new AudioBuffer({
        length: job.sourceBuffer.length + job.numLatencySamples,
        sampleRate,
        numberOfChannels: job.sourceBuffer.numberOfChannels
      });
      for (let i = 0; i < job.sourceBuffer.numberOfChannels; i++) {
        let ch = job.sourceBuffer.getChannelData(i);
        job.adjustedSourceBuffer.copyToChannel(ch, i, 0);
      }
      let stretchedLength = Math.ceil(job.adjustedSourceBuffer.length / job.rate);
      let oac = new OfflineAudioContext({
        length: stretchedLength,
        sampleRate,
        numberOfChannels: job.adjustedSourceBuffer.numberOfChannels
      });
      await oac.audioWorklet.addModule(job.workletUrl);
      let semitoneShift = 12 * Math.log2(job.rate);
      let settings = {
        "rate": 1,
        "semitones": job.semitones - semitoneShift,
        "tonality": 8e3,
        "block": 120,
        "overlap": 4
      };
      let stretch = await SignalsmithStretch(oac);
      let callback = (e) => {
        if (e.data === "config received") {
          stretch.port.removeEventListener("message", callback);
          _SignalSmithManager._processAudio(job, stretch, oac);
        }
      };
      stretch.port.addEventListener("message", callback);
      stretch.port.postMessage({ controls: settings });
    } catch (err) {
      console.log(err);
      job.reject(err);
    }
    _isProcessing = false;
    _SignalSmithManager._process();
  }
  static async _processAudio(job, stretch, oac) {
    let bsn = oac.createBufferSource();
    bsn.buffer = job.adjustedSourceBuffer;
    bsn.connect(stretch);
    stretch.connect(oac.destination);
    if (job.rate != 1) {
      bsn.playbackRate.value = job.rate;
    }
    bsn.start(0);
    let rendered = await oac.startRendering();
    bsn.disconnect(stretch);
    stretch.disconnect(oac.destination);
    let truncated = new AudioBuffer({
      length: rendered.length - job.numLatencySamples,
      sampleRate: rendered.sampleRate,
      numberOfChannels: rendered.numberOfChannels
    });
    for (let i = 0; i < rendered.numberOfChannels; i++) {
      let ch = rendered.getChannelData(i);
      ch = ch.slice(job.numLatencySamples);
      truncated.copyToChannel(ch, i);
    }
    job.resolve(truncated);
  }
  /**only intended for testing */
  static async renderAudioLive(workletUrl, audioBuffer, semitones = 0, rate = 1) {
    try {
      let oac = AudioContextManager.context;
      await oac.audioWorklet.addModule(workletUrl);
      let bsn = oac.createBufferSource();
      bsn.buffer = audioBuffer;
      let settings = {
        "rate": 1,
        "semitones": semitones,
        "tonality": 8e3,
        "block": 120,
        "overlap": 4
      };
      let stretch = await SignalsmithStretch(oac);
      stretch.port.postMessage({ controls: settings });
      if (rate != 1) {
        bsn.playbackRate.value = rate;
      }
      bsn.connect(stretch);
      stretch.connect(oac.destination);
      bsn.start(0);
      bsn.connect(oac.destination);
      return null;
    } catch (err) {
      console.log(err);
      return null;
    }
  }
};
/**if set, an iframe will be used so that memory cleanup is possible. Note
 * that the SignalsmightStretch.js file must be in the same folder and
 * level as the iframe html page. Not used if null.*/
_SignalSmithManager.iframeUrl = null;
var SignalSmithManager = _SignalSmithManager;

// src/utils/SoundLoader.ts
import { createEmitter as createEmitter2 } from "ts_utils";
var _SoundLoader = class _SoundLoader {
  /**listen for progress in downloading (not parsing) the SoundFont file.
   * Use (loaded / total) properties to calculate download ratio.*/
  static get downloadProgress() {
    return _SoundLoader._downloadProgressEmitter.event;
  }
  // static StreamController<html.ProgressEvent> _downloadProgressController = 
  // 	new StreamController<html.ProgressEvent>.broadcast();
  constructor() {
  }
  /**loads an audio file and returns an AudioBuffer object
   * @fileInputId - the id from the html input element
   * @context - an AudioContext object*/
  static async getAudioBufferFromFileInput(fileInputId, context) {
    let input = document.getElementById(fileInputId);
    if (input == null) {
      console.log("invalid FileInput element ID");
      return null;
    }
    if (input.files.length !== 1) {
      console.log("one audio file must be selected.");
      return null;
    }
    let file = input.files[0];
    let audioBuffer = await _SoundLoader.getAudioBufferFromBlob(
      file,
      context
    );
    return audioBuffer;
  }
  /**converts an audioBlob (typically from getOggAudioBlob()) into an
   * AudioBuffer object that can be used with an AudioBufferSourceNode.
   * After making a recording (or reloading one from a server), use
   * getOggAudioBlob() to get the recording as an html.Blob object. Then
   * pass that to this getAudioBuffer() method and wait for the result
   * of the future, which will be the AudioBuffer object. To play it, use
   * something like:
   * let playbackNode = context.createBufferSource();
   * _playbackNode.buffer = audioBuffer;
   * _playbackNode.connectNode(context.destination);
   * _playbackNode.start(0);*/
  static getAudioBufferFromBlob(audioBlob, context) {
    if (context == null)
      throw "AudioContext not set";
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.addEventListener("loadend", async (e) => {
        try {
          let ab = await context.decodeAudioData(
            reader.result
          );
          resolve(ab);
        } catch (err) {
          console.log("problem decoding file - unsupported type?");
          resolve(null);
        }
      });
      reader.readAsArrayBuffer(audioBlob);
    });
  }
  /**loads an audio file from a URL and converts it to an
   * AudioBuffer object that can be used with an AudioBufferSourceNode.
   * To play it, use something like:
   * let playbackNode = context.createBufferSource();
   * _playbackNode.buffer = audioBuffer;
   * _playbackNode.connectNode(context.destination);
   * _playbackNode.start(0);*/
  static getAudioBufferFromUrl(url, context) {
    if (context == null)
      throw "AudioContext not set";
    return new Promise((resolve, reject) => {
      _SoundLoader.getArrayBufferFromUrl(url).then((byteBuffer) => {
        context.decodeAudioData(byteBuffer).then((ab) => {
          resolve(ab);
        }).catch((err) => {
          console.log(err.toString());
          reject(err);
        });
      });
    });
  }
  /**loads a url as binary content, returning a ByteBuffer (Dart equivalent
   * of ArrayBuffer)*/
  static getArrayBufferFromUrl(url) {
    return new Promise((resolve, reject) => {
      let request = new XMLHttpRequest();
      request.responseType = "arraybuffer";
      request.onprogress = (e) => {
        _SoundLoader._downloadProgressEmitter.emit(e);
      };
      request.onload = (e) => {
        resolve(request.response);
      };
      request.onerror = (e) => {
        console.log(e.toString());
        reject(e.toString());
      };
      request.open("GET", url);
      request.send();
    });
  }
  static getDownloadLinkForArrayBuffer(data, fileName) {
    let a = document.createElement("a");
    a.style.display = "none";
    let blob = new Blob(data, { type: "octet/stream" });
    let url = URL.createObjectURL(blob);
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
    return a;
  }
  /**listen for progress in downloading (not parsing) the SoundFont file.
   * Use (loaded / total) properties to calculate download ratio.*/
  // static Stream<html.ProgressEvent> get onDownloadProgress => 
  // 	_downloadProgressController.stream;
};
/**used for listening to file download progress events*/
_SoundLoader._downloadProgressEmitter = createEmitter2();
var SoundLoader = _SoundLoader;

// src/utils/soundTouch/SoundTouchManager.ts
var SoundTouchManager = class {
  constructor() {
    this._onMessage = (e) => {
      let msgType = e.data[0];
      switch (msgType) {
        case "ready": {
          if (this._workerLoadedResolver) {
            this._workerLoadedResolver();
            this._workerLoadedResolver = null;
          }
          break;
        }
        case "render_complete": {
          this._handleFinishedRender(e.data[1]);
          break;
        }
      }
    };
  }
  /** Loads a SoundTouch module script
   * @param url the url of a SoundTouch module script matching the public
   * exports of the version packed with this application. */
  loadSoundTouchWorker(url) {
    return new Promise((resolve, reject) => {
      this._workerLoadedResolver = resolve;
      if (this._worker != null) {
        this._worker.removeEventListener("message", this._onMessage);
      }
      this._worker = new Worker(url);
      this._worker.addEventListener("message", this._onMessage);
    });
  }
  renderAudio(audioBuffer, { pitch = 1, tempo = 1 }) {
    if (this._currentAudioBuffer != null) {
      throw "You must wait for the previous render to finish";
    }
    this._currentAudioBuffer = audioBuffer;
    return new Promise((resolve, reject) => {
      this._renderFinishedResolver = resolve;
      let left = audioBuffer.getChannelData(0);
      let right = audioBuffer.numberOfChannels > 1 ? audioBuffer.getChannelData(1) : audioBuffer.getChannelData(0);
      this._worker.postMessage([
        "render",
        left,
        right,
        audioBuffer.sampleRate,
        audioBuffer.numberOfChannels,
        pitch,
        tempo
      ]);
    });
  }
  _handleFinishedRender(samples) {
    let ab = new AudioBuffer({
      length: samples[0].length,
      sampleRate: this._currentAudioBuffer.sampleRate,
      numberOfChannels: samples.length
    });
    ab.copyToChannel(samples[0], 0);
    if (samples.length > 1) {
      ab.copyToChannel(samples[1], 1);
    }
    this._currentAudioBuffer = null;
    this._renderFinishedResolver(ab);
    this._renderFinishedResolver = null;
  }
};

// src/utils/soundTouch/SoundTouchAudioNode.ts
var SoundTouchAudioNode = class extends AudioWorkletNode {
  constructor(context, workletName = "soundtouch-worklet", audioBuffer) {
    super(
      context,
      workletName,
      {
        outputChannelCount: [audioBuffer.numberOfChannels]
      }
    );
    this._ready = false;
    this._onMessage = (e) => {
      let message = e.data.message;
      switch (message) {
        case "PROCESSOR_CONSTRUCTOR": {
          this._initializeProcessor();
          break;
        }
        case "PROCESSOR_READY": {
          this._ready = true;
          if (this._readyResolver) {
            this._readyResolver();
            this._readyResolver = null;
          }
          break;
        }
      }
    };
    this._onMessageError = (e) => {
      console.log(e);
    };
    this._audioBuffer = audioBuffer;
    this._init();
  }
  ready() {
    return new Promise((resolve, reject) => {
      if (this._ready) {
        resolve();
      } else {
        this._readyResolver = resolve;
      }
    });
  }
  setPitch(pitch = 1) {
    this._updateProperty("pitch", pitch);
  }
  setTempo(tempo = 1) {
    this._updateProperty("tempo", tempo);
  }
  _init() {
    this.port.onmessage = this._onMessage;
    this.port.onmessageerror = this._onMessageError;
  }
  _initializeProcessor() {
    let ab = this._audioBuffer;
    let settings = {
      sampleRate: ab.sampleRate,
      duration: ab.duration,
      bufferLength: ab.length,
      numberOfChannels: ab.numberOfChannels
    };
    let details = [
      settings,
      ab.getChannelData(0),
      ab.numberOfChannels > 1 ? ab.getChannelData(1) : ab.getChannelData(0)
    ];
    this.port.postMessage({
      message: "INITIALIZE_PROCESSOR",
      detail: details
    });
    this._audioBuffer = null;
  }
  _updateProperty(name, value) {
    this.port.postMessage({
      message: "SET_PIPE_PROP",
      detail: { name, value }
    });
  }
};

// src/utils/soundTouch/SoundTouchManager2.ts
var SoundTouchManager2 = class {
  constructor() {
  }
  /**at this time, only pitch is supported */
  async renderAudio(workletUrl, audioBuffer, { pitch = 1, tempo = 1, fadeOut = false }) {
    try {
      let oac = new OfflineAudioContext({
        length: audioBuffer.length,
        sampleRate: audioBuffer.sampleRate,
        numberOfChannels: audioBuffer.numberOfChannels
      });
      await oac.audioWorklet.addModule(workletUrl);
      let bsn = oac.createBufferSource();
      bsn.buffer = audioBuffer;
      let stNode = await this.createSoundTouchAudioNode(oac, audioBuffer);
      stNode.setPitch(pitch);
      bsn.connect(stNode);
      stNode.connect(oac.destination);
      bsn.start(0);
      let rendered = await oac.startRendering();
      for (let i = 0; i < rendered.numberOfChannels; i++) {
        let ch = rendered.getChannelData(i);
        let numSamples = ch.length;
        for (let j = 0; j < numSamples; j++) {
          if (isNaN(ch[j])) {
            ch[j] = 0;
          }
        }
        if (fadeOut) {
          let fadeSamples = 0.1 * rendered.sampleRate;
          let endIdx = -1;
          for (let j = numSamples - 1; j >= 0; j--) {
            if (endIdx === -1 && ch[j] !== 0) {
              endIdx = j;
            }
            if (endIdx !== -1) {
              ch[j] *= (endIdx - j) / fadeSamples;
              if (endIdx - j >= fadeSamples) {
                break;
              }
            }
          }
        }
      }
      return rendered;
    } catch (err) {
      console.log(err);
      return null;
    }
  }
  /** Loads an audio worklet processor on the provided context. This is only
   * necessary if you are playing the audio in real time, as opposed to using
   * the renderAudio method.
   * @param workletUrl the url of the audio worklet for processing */
  async loadSoundTouchAudioWorklet(workletUrl, audioContext) {
    await audioContext.audioWorklet.addModule(workletUrl);
  }
  /**This feature does not work correctly. Audio doesn't play full length. */
  async createSoundTouchAudioNode(context, audioBuffer) {
    let node = new SoundTouchAudioNode(
      context,
      "soundtouch-worklet",
      audioBuffer
    );
    await node.ready();
    return node;
  }
};

// src/utils/WaveWriter.ts
var WaveWriter = class _WaveWriter {
  getWaveData(audioBuffer) {
    let leftChannel = audioBuffer.getChannelData(0);
    let rightChannel = audioBuffer.numberOfChannels > 1 ? audioBuffer.getChannelData(1) : audioBuffer.getChannelData(0);
    let interleaved = this.interleave(leftChannel, rightChannel);
    let uint8Array = new Uint8Array(44 + interleaved.length * 2);
    let view = new DataView(uint8Array.buffer);
    this.writeUTFBytes(view, 0, "RIFF");
    view.setUint32(4, 44 + interleaved.length * 2, true);
    this.writeUTFBytes(view, 8, "WAVE");
    this.writeUTFBytes(view, 12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 2, true);
    view.setUint32(24, audioBuffer.sampleRate, true);
    view.setUint32(28, audioBuffer.sampleRate * 4, true);
    view.setUint16(32, 4, true);
    view.setUint16(34, 16, true);
    this.writeUTFBytes(view, 36, "data");
    view.setUint32(40, interleaved.length * 2, true);
    let lng = interleaved.length;
    let index = 44;
    for (let i = 0; i < lng; i++) {
      view.setInt16(index, Math.trunc(interleaved[i] * 32767), true);
      index += 2;
    }
    return view.buffer;
  }
  writeUTFBytes(view, offset, string) {
    let lng = string.length;
    for (let i = 0; i < lng; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }
  interleave(leftChannel, rightChannel) {
    let length = leftChannel.length + rightChannel.length;
    let result = new Float32Array(length);
    let inputIndex = 0;
    for (let index = 0; index < length; ) {
      result[index++] = leftChannel[inputIndex];
      result[index++] = rightChannel[inputIndex];
      inputIndex++;
    }
    return result;
  }
  /**helper method to generate a wave file from an AudioBuffer and pop a
   * file save dialog.
   * @param audioBuffer the audio to convert to a wave file
   * @param name the name of the file
   */
  static saveWaveFile(audioBuffer, name) {
    let waveWriter = new _WaveWriter();
    let waveAB = waveWriter.getWaveData(audioBuffer);
    let blob = new Blob([waveAB], { type: "audio/wav" });
    let link = document.createElement("a");
    let objectURL = URL.createObjectURL(blob);
    link.href = objectURL;
    link.href = URL.createObjectURL(blob);
    link.download = name.endsWith(".wav") ? name : `${name}.wav`;
    link.click();
  }
};
export {
  AudioContextManager,
  AudioProcessor,
  MultiSoundLoader,
  PhazeManager,
  SignalSmithManager,
  SoundLoader,
  SoundTouchManager,
  SoundTouchManager2,
  WaveWriter
};
